import { useEffect, useState } from "react";
import { AssessmentTabs } from "@/components/assessment/AssessmentTabs";
import { ResultsFilters } from "@/components/assessment/results/ResultsFilters";
import { ByAssetView } from "@/components/assessment/results/ByAssetView";
import { ByHazardView } from "@/components/assessment/results/ByHazardView";
import { getResults, type AssetHazardResult } from "@/lib/results";
import {
  listAssets,
  listAssetTypes,
  listValueChainStages,
  getWorkspaceValueChainImportance,
  type Asset,
  type AssetType,
  type ValueChainStage,
} from "@/lib/assets";
import {
  defaultResultsFilterState,
  filterAssets,
  orderedHorizons,
  type ResultsFilterState,
} from "@/lib/results-filters";
import { HAZARD_TYPES, type TimeHorizon } from "@shared/scoring/types";
import { Building2, Zap } from "lucide-react";
import { Segmented } from "@/components/ui/segmented";
import { HORIZON_LABELS } from "@/lib/results";

type ViewMode = "by-asset" | "by-hazard";

function ViewModeTabs({ mode, onChange }: { mode: ViewMode; onChange: (mode: ViewMode) => void }) {
  return (
    <div className="flex items-center gap-2.5">
      <span className="text-xs uppercase leading-4 tracking-[0.08em] text-muted-foreground">View by:</span>
      <Segmented
        variant="solid"
        options={[
          { value: "by-asset", label: "Asset", icon: Building2 },
          { value: "by-hazard", label: "Hazard", icon: Zap },
        ]}
        isActive={(v) => mode === v}
        onToggle={(v) => onChange(v as ViewMode)}
      />
    </div>
  );
}

function AssessmentDetailedResults() {
  const [assets, setAssets] = useState<Asset[] | null>(null);
  const [assetTypes, setAssetTypes] = useState<AssetType[]>([]);
  const [stages, setStages] = useState<ValueChainStage[]>([]);
  const [stageImportance, setStageImportance] = useState<Record<string, number>>({});
  const [results, setResults] = useState<AssetHazardResult[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<ResultsFilterState>(defaultResultsFilterState());
  const [viewMode, setViewMode] = useState<ViewMode>("by-asset");
  const [activeHorizonState, setActiveHorizonState] = useState<TimeHorizon>("historical");

  useEffect(() => {
    Promise.all([
      listAssets(),
      listAssetTypes(),
      listValueChainStages(),
      getWorkspaceValueChainImportance(),
      getResults(),
    ])
      .then(([assetsRes, assetTypesRes, stagesRes, importanceRes, resultsRes]) => {
        setAssets(assetsRes.assets);
        setAssetTypes(assetTypesRes.assetTypes);
        setStages(stagesRes.stages);
        const map: Record<string, number> = {};
        for (const rating of importanceRes.ratings) map[rating.valueChainStageId] = rating.importance;
        setStageImportance(map);
        setResults(resultsRes.results);
      })
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load results"));
  }, []);

  if (error) return <p className="text-sm font-medium text-destructive">{error}</p>;
  if (!assets) return <p className="text-sm text-muted-foreground">Loading...</p>;

  const filteredAssets = filterAssets(assets, filters, stageImportance);
  const filteredAssetIds = new Set(filteredAssets.map((a) => a.id));
  const filteredResults = results.filter((r) => filteredAssetIds.has(r.assetId));

  // Same clamping idiom as AssessmentResults.tsx's mapHorizon/effectiveMapHorizon —
  // this view shows one horizon at a time even though the shared filter is a
  // multi-select (see the design spec's "View-mode shell" section).
  const activeHorizons = orderedHorizons(filters.horizons);
  const activeHorizon = activeHorizons.includes(activeHorizonState) ? activeHorizonState : activeHorizons[0];

  // New decision, not a confirmation of documented pre-existing behavior:
  // ResultsFilterState.hazards' doc comment claims it already "scopes what's
  // rendered," but nothing currently consumes it. By analogy with every
  // other filter dimension in this app (empty array = no filter, show
  // everything), an empty hazards selection here means "show all 6
  // hazards" — spread HAZARD_TYPES (a readonly array) into a new mutable
  // array since ByAssetViewProps.visibleHazards is typed HazardType[], not
  // readonly.
  const visibleHazards = filters.hazards.length ? filters.hazards : [...HAZARD_TYPES];

  const horizonSummary = activeHorizons.map((h) => HORIZON_LABELS[h]).join(" vs ");
  const byAsset = viewMode === "by-asset";

  return (
    <div className="flex flex-col gap-6">
      <AssessmentTabs />

      <ResultsFilters filters={filters} onChange={setFilters} assets={assets} stages={stages} />

      <div className="flex flex-col items-start justify-between gap-4 lg:flex-row lg:items-center">
        <div className="flex flex-col gap-1">
          <h1 className="text-xl font-heading font-bold leading-7">
            Detailed results — by {byAsset ? "asset" : "hazard"}
          </h1>
          <p className="text-xs leading-4 text-muted-foreground">
            {byAsset
              ? "Select an asset to see its breakdown across every hazard"
              : "Select a hazard to see how every asset scores against it"}{" "}
            — {filters.scenario} · {horizonSummary}
          </p>
        </div>
        <ViewModeTabs mode={viewMode} onChange={setViewMode} />
      </div>

      {viewMode === "by-asset" && (
        <ByAssetView
          assets={filteredAssets}
          assetTypes={assetTypes}
          stages={stages}
          visibleHazards={visibleHazards}
          results={filteredResults}
          scenario={filters.scenario}
          activeHorizon={activeHorizon}
          activeHorizons={activeHorizons}
          onHorizonChange={setActiveHorizonState}
        />
      )}

      {viewMode === "by-hazard" && (
        <ByHazardView
          assets={filteredAssets}
          assetTypes={assetTypes}
          visibleHazards={visibleHazards}
          results={filteredResults}
          scenario={filters.scenario}
          activeHorizon={activeHorizon}
          activeHorizons={activeHorizons}
          onHorizonChange={setActiveHorizonState}
        />
      )}
    </div>
  );
}

export default AssessmentDetailedResults;
